import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface MarkdownRendererProps {
  source: string;
  /** Override react-markdown components (e.g. to intercept text nodes for chips). */
  components?: Components;
  /** Extra className applied to the wrapper div. */
  className?: string;
}

const DEFAULT_ALLOWED_SCHEME = /^(https?|mailto|tel|chatbot-product|chatbot-page):/i;

function defaultUrlTransform(url: string): string {
  if (!url) return '';
  // Permit relative URLs and the schemes listed above; drop anything else.
  if (url.startsWith('/') || url.startsWith('#')) return url;
  return DEFAULT_ALLOWED_SCHEME.test(url) ? url : '';
}

/**
 * Base react-markdown wrapper with remark-gfm and safe defaults:
 * - Links open in a new tab with rel="noopener noreferrer"
 * - Raw HTML is disabled (default) — backend body_md is plain markdown
 *
 * For sections that may contain [product:slug] / [page:slug] chips, use
 * the chip-aware renderer in `chips.tsx`, which composes this and adds
 * a `text` component override.
 */
export function MarkdownRenderer({
  source,
  components,
  className,
}: MarkdownRendererProps): JSX.Element {
  const defaultComponents: Components = {
    a: ({ href, children, ...props }) => (
      <a
        {...props}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="tw-text-cal-green-700 tw-font-medium tw-underline tw-decoration-cal-green-200 tw-decoration-1 tw-underline-offset-2 hover:tw-text-cal-green-900 hover:tw-decoration-cal-green-500"
      >
        {children}
      </a>
    ),
    h1: ({ children }) => (
      <h1 className="tw-font-serif tw-text-lg tw-font-semibold tw-text-cal-ink tw-mt-3 tw-mb-2">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="tw-font-serif tw-text-[16px] tw-font-semibold tw-text-cal-ink tw-mt-3 tw-mb-2">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="tw-font-serif tw-text-[15px] tw-font-semibold tw-text-cal-ink tw-mt-3 tw-mb-1.5">
        {children}
      </h3>
    ),
    p: ({ children }) => <p className="tw-leading-relaxed tw-mb-2">{children}</p>,
    ul: ({ children }) => <ul className="tw-list-disc tw-pl-5 tw-mb-2 tw-space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="tw-list-decimal tw-pl-5 tw-mb-2 tw-space-y-1">{children}</ol>,
    li: ({ children }) => <li className="tw-leading-relaxed">{children}</li>,
    strong: ({ children }) => (
      <strong className="tw-font-semibold tw-text-cal-ink">{children}</strong>
    ),
    em: ({ children }) => <em className="tw-italic">{children}</em>,
    code: ({ children }) => (
      <code className="tw-bg-cal-green-50 tw-text-cal-green-700 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[12px] tw-font-mono">
        {children}
      </code>
    ),
  };

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{ ...defaultComponents, ...components }}
        urlTransform={defaultUrlTransform}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
